import type {
  CapabilityState,
  CloudflareIdentityProvider,
  CloudflarePostureFinding,
  CloudflareScimConnection,
} from "../shared/types";
import { EmptyState } from "./AdminUi";
import { PaginationControls, usePagination } from "./pagination";

export function CloudflarePostureSection({
  identityProviders,
  findings,
  identityCapability,
  scimConnections,
  onManageScim,
}: {
  identityProviders: CloudflareIdentityProvider[];
  findings: CloudflarePostureFinding[];
  identityCapability: CapabilityState;
  scimConnections: CloudflareScimConnection[];
  onManageScim: (identityProvider: CloudflareIdentityProvider) => void;
}) {
  const [findingSlice, setFindingPage] = usePagination(findings, 8);
  return (
    <div className="cloudflare-summary-grid">
      <section className="panel-card cloudflare-identity-card">
        <header className="panel-card__header">
          <div>
            <span className="eyebrow">Directory bridge</span>
            <h2>Identity &amp; provisioning</h2>
          </div>
          <span
            className={`panel-status panel-status--${identityCapability === "available" ? "positive" : "warning"}`}
          >
            {identityCapability === "available"
              ? "Live"
              : identityCapability === "degraded"
                ? "Unavailable"
                : "Not configured"}
          </span>
        </header>
        {identityProviders.length ? (
          <div className="identity-provider-stack">
            {identityProviders.map((identityProvider) => {
              const connection = scimConnections.find(
                (candidate) =>
                  candidate.identityProviderId === identityProvider.id,
              );
              return (
                <article
                  className="identity-provider-card"
                  key={identityProvider.id}
                >
                  <h3>{identityProvider.name}</h3>
                  <dl className="cloudflare-facts">
                    <div>
                      <dt>Protocol</dt>
                      <dd>
                        <strong>{identityProvider.type.toUpperCase()}</strong>
                        <small>
                          {identityProvider.emailClaim ?? "Default email claim"}
                        </small>
                      </dd>
                    </div>
                    <div>
                      <dt>Claims</dt>
                      <dd className="tag-list">
                        {identityProvider.claims.length
                          ? identityProvider.claims.map((claim) => (
                              <span className="tag" key={claim}>
                                {claim}
                              </span>
                            ))
                          : "None imported"}
                      </dd>
                    </div>
                    <div>
                      <dt>SCIM</dt>
                      <dd>
                        <strong>
                          {identityProvider.scim.enabled
                            ? "Enabled"
                            : "Disabled"}
                        </strong>
                        <small>
                          {identityProvider.scim.enabled
                            ? `${identityProvider.scim.identityUpdateBehavior} identity updates`
                            : "OIDC claims update at next login"}
                        </small>
                      </dd>
                    </div>
                    <div>
                      <dt>Credential</dt>
                      <dd>
                        <strong>
                          {connection
                            ? `${connection.source === "managed" ? "Encrypted" : "Environment"} credential`
                            : "Not stored"}
                        </strong>
                        <small>
                          {connection
                            ? connection.endpoint
                            : "Import the one-time Cloudflare SCIM token"}
                        </small>
                      </dd>
                    </div>
                    <div>
                      <dt>Deprovisioning</dt>
                      <dd>
                        <strong>
                          {identityProvider.scim.userDeprovision
                            ? "Session revoke enabled"
                            : "No automatic revoke"}
                        </strong>
                        <small>
                          {identityProvider.scim.seatDeprovision
                            ? "Zero Trust seat is also removed"
                            : "Seat remains assigned"}
                        </small>
                      </dd>
                    </div>
                  </dl>
                  <footer className="identity-provider-card__actions">
                    <button
                      className="button button--secondary button--compact"
                      onClick={() => onManageScim(identityProvider)}
                      type="button"
                    >
                      Manage SCIM
                    </button>
                  </footer>
                </article>
              );
            })}
          </div>
        ) : (
          <EmptyState
            compact
            hint="Configure the IdP binding and read permission."
            title="No identity provider details"
          />
        )}
      </section>

      <section className="panel-card cloudflare-findings-card">
        <header className="panel-card__header">
          <div>
            <span className="eyebrow">Continuous checks</span>
            <h2>Posture findings</h2>
          </div>
          <span className="panel-status">{findings.length}</span>
        </header>
        {findings.length ? (
          <ol className="finding-list">
            {findingSlice.items.map((finding) => (
              <li key={`${finding.code}:${finding.resourceId}`}>
                <span
                  className={`finding-severity finding-severity--${finding.severity}`}
                >
                  {finding.severity}
                </span>
                <div>
                  <strong>{finding.title}</strong>
                  <p>{finding.detail}</p>
                  {(finding.expected || finding.actual) && (
                    <small>
                      {finding.expected && `Expected: ${finding.expected}`}
                      {finding.expected && finding.actual && " · "}
                      {finding.actual && `Current: ${finding.actual}`}
                    </small>
                  )}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <EmptyState
            compact
            hint="Identity routing and Access policies are aligned."
            title="No configuration drift found"
          />
        )}
        <PaginationControls
          label="findings"
          onPageChange={setFindingPage}
          slice={findingSlice}
        />
      </section>
    </div>
  );
}
